/**
 * A casca do painel — spec 0087.
 *
 * O menu do topo em três andares (setor, módulo, aba) sobre URLs: cada tela tem
 * endereço, o F5 fica onde está, o Voltar do navegador volta, e um link colado
 * abre a tela — ou diz por que não abre. A árvore, as regras de quem vê o quê
 * e as URLs antigas moram em `config/menu.ts`; aqui só se desenha.
 *
 * Substitui as duas navegações que o painel tinha: o `Dashboard.tsx` (o topo,
 * sem URL) e a barra lateral (com URL, outra árvore e outras permissões).
 */
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { getUser } from '../../services/auth';
import {
  MENU, abasDaFaixa, acessoDe, caminhoDoModulo, caminhoDoSetor, entradaDe, menuDe, resolver, tituloDaTela,
  type Modulo,
} from '../../config/menu';
import { useContadores } from '../../config/contadores';
import { ROUTE_COMPONENTS } from '../../config/routeComponents';
import { Carregando, EstadoVazio } from '../common/interface';
import { MenuDaConta } from './MenuDaConta';
import { MeusDados } from './MeusDados';
import { BotaoFeedback } from '../feedback/BotaoFeedback';
import { registrarFonteDoFeedback } from '../feedback/contador';
import { registrarFonteDasPlanilhas } from '../corporativo/monitor/planilhas-escolas/contador';
import { registrarFonteDasNotas } from '../corporativo/processos/notas-fiscais-cooperado/contador';
import type { AlvoPessoa } from '../corporativo/cadastros/CadastrosPessoas';
import type { AlvoVeiculo } from '../corporativo/cadastros/CadastrosVeiculos';
import './casca.css';

const TITULO_BASE = 'Coopertransmig :: Tresco';

/**
 * O que as Pendências de Cadastro (spec 0028) mandam para o cadastro que
 * corrige. A tela de pendências diagnostica; quem corrige são os cadastros, em
 * outro setor. O pedido chega por evento, e a casca o segura até o cadastro
 * montar (ele é carregado sob demanda, e um ouvinte lá dentro perderia o disparo).
 */
type AlvoCadastro = (AlvoPessoa | AlvoVeiculo) & { destino: 'pessoas' | 'veiculos' };

interface Props {
  onLogout: () => void;
}

export function Casca({ onLogout }: Props) {
  const { permissoes, isAdmin } = usePermissions();
  const menu = useMemo(() => menuDe(acessoDe(permissoes, isAdmin)), [permissoes, isAdmin]);
  const entrada = entradaDe(menu);

  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const resolucao = useMemo(() => resolver(pathname, menu, MENU, search), [pathname, search, menu]);

  // O número do que pede ação, no setor do endereço — se a pessoa o vê. Antes
  // de qualquer retorno: é hook (spec 0098, DD-03).
  const [sId, mId] = pathname.split('/').filter(Boolean);
  // O número da fila do Feedback (0108) só para quem vê a fila: para os outros
  // a rota responderia 403 a cada minuto.
  const atende = useMemo(() => acessoDe(permissoes, isAdmin).tem('api:feedback:atender'), [permissoes, isAdmin]);
  useEffect(() => { if (atende) registrarFonteDoFeedback(); }, [atende]);
  // O das planilhas das escolas (0111), só para quem vê a tela.
  const veAsPlanilhas = useMemo(() => acessoDe(permissoes, isAdmin).tem('api:transportador:read'), [permissoes, isAdmin]);
  useEffect(() => { if (veAsPlanilhas) registrarFonteDasPlanilhas(); }, [veAsPlanilhas]);
  // O das notas fiscais dos PJ (0113), só para quem vê o Pagamento.
  const veOPagamento = useMemo(() => acessoDe(permissoes, isAdmin).tem('api:pagamentos:read'), [permissoes, isAdmin]);
  useEffect(() => { if (veOPagamento) registrarFonteDasNotas(); }, [veOPagamento]);
  const contadores = useContadores(menu.some((s) => s.id === sId) ? sId : undefined);

  const [meusDados, setMeusDados] = useState(false);
  const botaoDaConta = useRef<HTMLButtonElement>(null);
  const [alvo, setAlvo] = useState<AlvoCadastro | null>(null);
  const esquecerAlvo = useCallback(() => setAlvo(null), []);

  useEffect(() => {
    const aoCorrigir = (e: Event) => {
      const pedido = (e as CustomEvent).detail as AlvoCadastro | undefined;
      if (!pedido?.destino) return;
      setAlvo(pedido);
      navigate(`/corporativo/cadastros/${pedido.destino}`);
    };
    window.addEventListener('cadastro-corrigir', aoCorrigir);
    return () => window.removeEventListener('cadastro-corrigir', aoCorrigir);
  }, [navigate]);

  // O título da aba do navegador diz a tela e o lugar ("Pedidos · Passageiros",
  // spec 0098): é o que aparece no histórico e no menu do Voltar, e o que
  // distingue duas abas do painel abertas lado a lado.
  const nomeDaTela = resolucao.tipo === 'tela' ? (resolucao.aba?.rotulo ?? resolucao.modulo.rotulo) : null;
  const tituloDoNavegador = resolucao.tipo === 'tela' ? tituloDaTela(resolucao.setor, resolucao.modulo, resolucao.aba) : null;
  useEffect(() => {
    document.title = tituloDoNavegador ? `${tituloDoNavegador} · ${TITULO_BASE}` : TITULO_BASE;
    return () => { document.title = TITULO_BASE; };
  }, [tituloDoNavegador]);

  // A busca já vem no destino: o `resolver` é o dono dela.
  if (resolucao.tipo === 'ir') return <Navigate to={resolucao.para} replace />;

  const nome = getUser()?.nomeUsuario || 'Usuário';
  // A tela que vai junto com o feedback (FR-005): o caminho no menu, como a pessoa o lê.
  const telaDoFeedback = resolucao.tipo === 'tela'
    ? [resolucao.setor.rotulo, resolucao.modulo.rotulo, resolucao.aba?.rotulo].filter(Boolean).join(' › ')
    : pathname;
  const feedback = <BotaoFeedback tela={telaDoFeedback} />;
  const conta = (
    <MenuDaConta nome={nome} gatilho={botaoDaConta} onMeusDados={() => setMeusDados(true)} onSair={onLogout} />
  );
  // A janela abre de um item do menu, que some ao fechar o menu: sem isto, o
  // foco voltaria para o nada ao fechar a janela. Volta ao botão da conta.
  const fecharMeusDados = () => {
    setMeusDados(false);
    requestAnimationFrame(() => botaoDaConta.current?.focus());
  };
  const janelaMeusDados = <MeusDados aberta={meusDados} onFechar={fecharMeusDados} />;

  // Quem não tem nenhuma tela no painel: o perfil é do aplicativo.
  if (menu.length === 0) {
    return (
      <div className="casca">
        <header className="casca-topo">
          <span className="casca-marca"><img src="/assets/logo-cooper.png" alt="Coopertransmig" /></span>
          <span className="casca-espaco" />
          {feedback}
          {conta}
        </header>
        <main id="conteudo" className="casca-conteudo">
          <EstadoVazio
            titulo="O seu acesso é pelo aplicativo"
            acao={<button type="button" className="fc-btn fc-btn-secundario" onClick={onLogout}>Sair</button>}
          >
            O seu perfil não inclui telas do painel web. Use o aplicativo Tresco no celular.
          </EstadoVazio>
        </main>
        {janelaMeusDados}
      </div>
    );
  }

  // O que está aceso no menu: a tela aberta, ou, sem acesso, o setor e o
  // módulo do endereço, se a pessoa os vê.
  const setor = resolucao.tipo === 'tela' ? resolucao.setor : menu.find((s) => s.id === sId);
  const modulo = resolucao.tipo === 'tela' ? resolucao.modulo : setor?.modulos.find((m) => m.id === mId);
  const faixa = modulo ? abasDaFaixa(modulo) : [];
  // O número vai com aria-hidden e o nome do link diz a frase: sem região viva,
  // que anunciaria a cada leitura do relógio.
  const numero = (n: number) => (n > 0 ? (
    <><span className="casca-contador" aria-hidden="true">{n}</span><span className="casca-so-leitor">, {n} pedindo ação</span></>
  ) : null);
  // O módulo soma as abas dele: a faixa só aparece para o módulo aberto.
  const doModulo = (m: Modulo) => abasDaFaixa(m).reduce((t, a) => t + (a.contador ? contadores[a.contador] ?? 0 : 0), 0);

  const Tela = resolucao.tipo === 'tela' ? ROUTE_COMPONENTS[resolucao.componente] : null;
  const propsDaTela = resolucao.tipo === 'tela' && (resolucao.componente === 'CadastrosPessoas' || resolucao.componente === 'CadastrosVeiculos')
    ? {
        alvo: alvo && alvo.destino === (resolucao.componente === 'CadastrosPessoas' ? 'pessoas' : 'veiculos') ? alvo : null,
        onAlvoConsumido: esquecerAlvo,
      }
    : {};

  return (
    <div className="casca">
      <a className="casca-pular" href="#conteudo">Pular para o conteúdo</a>

      <header className="casca-topo">
        <Link to={entrada ?? '/'} className="casca-marca" aria-label="Coopertransmig, tela inicial">
          <img src="/assets/logo-cooper.png" alt="" />
        </Link>
        <nav className="casca-setores" aria-label="Setores">
          {menu.map((s) => (
            <Link
              key={s.id}
              to={caminhoDoSetor(s)}
              className="casca-setor"
              aria-current={s.id === setor?.id ? 'true' : undefined}
            >
              {s.rotulo}
            </Link>
          ))}
        </nav>
        {feedback}
        {conta}
      </header>

      {setor && (
        <nav className="casca-modulos" aria-label={`Módulos de ${setor.rotulo}`}>
          {setor.modulos.map((m) => (
            <Link
              key={m.id}
              to={caminhoDoModulo(setor, m)}
              className="casca-modulo"
              aria-current={m.id === modulo?.id ? (m.abas ? 'true' : 'page') : undefined}
            >
              {m.rotulo}
              {numero(doModulo(m))}
            </Link>
          ))}
        </nav>
      )}

      {/* Uma aba sozinha não é escolha: a faixa só aparece com duas ou mais. */}
      {setor && modulo && faixa.length > 1 && (
        <nav className="casca-abas" aria-label={`Telas de ${modulo.rotulo}`}>
          {faixa.map((a) => (
            <Link
              key={a.id}
              to={`/${setor.id}/${modulo.id}/${a.id}`}
              className="casca-aba"
              aria-current={resolucao.tipo === 'tela' && a.id === resolucao.aba?.id ? 'page' : undefined}
            >
              {a.rotulo}
              {a.contador && numero(contadores[a.contador] ?? 0)}
            </Link>
          ))}
        </nav>
      )}

      {/* A chave é o endereço: a mesma tela em dois módulos (Parâmetros de
          emissão, no CT-e e na ATF) monta de novo ao trocar, como antes. */}
      <main id="conteudo" className="casca-conteudo" key={pathname} tabIndex={-1}>
        {Tela ? (
          <Suspense fallback={<Carregando rotulo={`Carregando ${nomeDaTela ?? 'a tela'}`} />}>
            <Tela {...propsDaTela} />
          </Suspense>
        ) : (
          <EstadoVazio
            titulo={resolucao.tipo === 'tela' ? 'Esta tela não pôde ser aberta' : 'Você não tem acesso a esta tela'}
            acao={entrada && <Link className="fc-btn fc-btn-secundario" to={entrada}>Ir para a tela inicial</Link>}
          >
            {resolucao.tipo === 'tela'
              ? 'O painel não encontrou o componente desta tela. Avise quem mantém o sistema.'
              : 'O endereço existe, mas o seu perfil não inclui esta tela. Se você precisa dela, peça a quem administra os perfis.'}
          </EstadoVazio>
        )}
      </main>

      {janelaMeusDados}
    </div>
  );
}
